import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Plus, MessageSquare, PhoneCall, Mail, Eye, Send, Handshake, ChevronDown, ChevronUp, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const followUpIcons = { text: MessageSquare, call: PhoneCall, card: Mail, visit: Eye, social_media: Send };
const followUpTypes = ['text', 'call', 'card', 'visit', 'social_media'];
const typeBadge = (t) => {
  const map = { regular: 'border-blue-200 text-blue-700 bg-blue-50', visitor: 'border-amber-200 text-amber-700 bg-amber-50', occasional: 'border-teal-200 text-teal-700 bg-teal-50', inactive: 'border-slate-300 text-slate-500 bg-slate-50' };
  return map[t] || 'border-slate-200 text-slate-600';
};

function PeopleList({ people, ministries, navigate, onAddFollowUp }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ministryFilter, setMinistryFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  const filtered = useMemo(() => people.filter(p => {
    if (search) { const q = search.toLowerCase(); if (!p.first_name.toLowerCase().includes(q) && !p.last_name.toLowerCase().includes(q)) return false; }
    if (typeFilter !== 'all' && p.person_type !== typeFilter) return false;
    if (ministryFilter !== 'all' && !p.ministries?.includes(ministryFilter)) return false;
    return true;
  }), [people, search, typeFilter, ministryFilter]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="touchpoint-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name..." className="pl-10 border-slate-300" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="touchpoint-type-filter" className="w-36 border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="visitor">Visitor</SelectItem>
              <SelectItem value="occasional">Occasional</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ministryFilter} onValueChange={setMinistryFilter}>
            <SelectTrigger data-testid="touchpoint-ministry-filter" className="w-44 border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ministries</SelectItem>
              {ministries.map(m => <SelectItem key={m.ministry_id} value={m.name}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.map(p => {
        const isExpanded = expanded[p.person_id];
        return (
          <Card key={p.person_id} data-testid={`touchpoint-row-${p.person_id}`} className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600 shrink-0 cursor-pointer" onClick={() => navigate(`/people/${p.person_id}`)}>
                  {p.first_name[0]}{p.last_name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900 cursor-pointer hover:text-blue-700 transition-colors" onClick={() => navigate(`/people/${p.person_id}`)}>{p.first_name} {p.last_name}</span>
                    <Badge variant="outline" className={`text-xs ${typeBadge(p.person_type)}`}>{p.person_type}</Badge>
                    {p.ministries?.slice(0, 2).map(m => <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>)}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span>{p.followup_count || 0} follow-ups</span>
                    {p.last_followup_date && <span>Last: {new Date(p.last_followup_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button data-testid={`touchpoint-add-fu-${p.person_id}`} size="sm" onClick={() => onAddFollowUp(p)} className="bg-blue-700 hover:bg-blue-800 h-8 text-xs">
                    <Plus className="h-3 w-3 mr-1" />Log
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setExpanded(prev => ({ ...prev, [p.person_id]: !prev[p.person_id] }))} className="h-8 w-8 p-0">
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                  {(!p.recent_followups || p.recent_followups.length === 0) && <p className="text-xs text-slate-400 italic">No follow-up history yet</p>}
                  {p.recent_followups?.map(fu => {
                    const FuIcon = followUpIcons[fu.action_type] || MessageSquare;
                    return (
                      <div key={fu.followup_id} className="flex items-start gap-3 px-2 py-1.5 rounded bg-slate-50">
                        <FuIcon className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] capitalize h-5">{fu.action_type.replace('_', ' ')}</Badge>
                            <span className="text-[10px] text-slate-400">{new Date(fu.date + 'T00:00:00').toLocaleDateString()}</span>
                            <span className="text-[10px] text-slate-400">by {fu.performed_by_name}</span>
                          </div>
                          {fu.note && <p className="text-xs text-slate-600 mt-0.5">{fu.note}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      {filtered.length === 0 && (
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-8 text-center"><Handshake className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No people match your filters</p></CardContent></Card>
      )}
    </div>
  );
}

export default function Touchpoint() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [people, setPeople] = useState([]);
  const [digest, setDigest] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fuOpen, setFuOpen] = useState(false);
  const [fuPerson, setFuPerson] = useState(null);
  const [fuForm, setFuForm] = useState({ action_type: 'call', date: new Date().toISOString().slice(0, 10), note: '' });

  useEffect(() => {
    Promise.all([
      api.get('/touchpoints'),
      api.get('/touchpoints/digest?days=30'),
      api.get('/ministries'),
    ]).then(([t, d, m]) => {
      setPeople(t.data);
      setDigest(d.data);
      setMinistries(m.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const openFollowUp = (person) => {
    setFuPerson(person);
    setFuForm({ action_type: 'call', date: new Date().toISOString().slice(0, 10), note: '' });
    setFuOpen(true);
  };

  const addFollowUp = async () => {
    if (!fuPerson) return;
    try {
      const res = await api.post(`/followups/${fuPerson.person_id}`, fuForm);
      setPeople(prev => prev.map(p => {
        if (p.person_id !== fuPerson.person_id) return p;
        return { ...p, recent_followups: [res.data, ...(p.recent_followups || [])].slice(0, 5), followup_count: (p.followup_count || 0) + 1, last_followup_date: fuForm.date };
      }));
      setDigest(prev => prev.filter(d => d.person_id !== fuPerson.person_id));
      setFuOpen(false);
      toast.success('Follow-up logged');
    } catch { toast.error('Failed to log follow-up'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 animate-fade-in">
      <Tabs defaultValue="all">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="all" data-testid="touchpoint-tab-all"><Handshake className="h-3 w-3 mr-1" />All People</TabsTrigger>
          <TabsTrigger value="digest" data-testid="touchpoint-tab-digest">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Needs Attention
            {digest.length > 0 && <Badge className="ml-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] h-5 px-1.5">{digest.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <PeopleList people={people} ministries={ministries} navigate={navigate} onAddFollowUp={openFollowUp} />
        </TabsContent>

        <TabsContent value="digest" className="mt-4">
          <Card className="border-amber-200 bg-amber-50/30 mb-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Weekly Touchpoint Digest</p>
                    <p className="text-xs text-amber-700">{digest.length} people need attention</p>
                  </div>
                </div>
                {digest.length > 0 && (
                  <Button data-testid="clear-all-touchpoints" variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={async () => {
                    if (!window.confirm('Mark everyone as contacted today? This will clear the entire digest.')) return;
                    try {
                      await api.post('/touchpoints/clear-all');
                      setDigest([]);
                      toast.success('All touchpoints cleared');
                    } catch { toast.error('Failed'); }
                  }}>Clear All</Button>
                )}
              </div>
            </CardContent>
          </Card>
          <div className="space-y-3" data-testid="touchpoint-digest-list">
            {digest.map(p => (
              <Card key={p.person_id} className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-sm font-medium text-amber-800 shrink-0 cursor-pointer" onClick={() => navigate(`/people/${p.person_id}`)}>
                      {p.first_name[0]}{p.last_name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 cursor-pointer hover:text-blue-700" onClick={() => navigate(`/people/${p.person_id}`)}>{p.first_name} {p.last_name}</span>
                        <Badge variant="outline" className={`text-xs ${typeBadge(p.person_type)}`}>{p.person_type}</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        {p.phone && <span>{p.phone}</span>}
                        <span className="text-amber-600 font-medium">
                          {p.days_since_contact != null ? `${p.days_since_contact} days since last contact` : 'Never contacted'}
                        </span>
                      </div>
                    </div>
                    <Button data-testid={`digest-add-fu-${p.person_id}`} size="sm" onClick={() => openFollowUp(p)} className="bg-blue-700 hover:bg-blue-800 h-8 text-xs shrink-0">
                      <Plus className="h-3 w-3 mr-1" />Reach Out
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {digest.length === 0 && (
              <Card className="border-emerald-200 shadow-sm bg-emerald-50/30"><CardContent className="p-8 text-center"><Handshake className="h-12 w-12 text-emerald-400 mx-auto mb-3" /><p className="text-emerald-700 font-medium">Everyone has been contacted recently!</p></CardContent></Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={fuOpen} onOpenChange={setFuOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">Log Follow-Up for {fuPerson?.first_name} {fuPerson?.last_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label>Action Type</Label>
              <Select value={fuForm.action_type} onValueChange={v => setFuForm(f => ({ ...f, action_type: v }))}>
                <SelectTrigger data-testid="touchpoint-fu-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{followUpTypes.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input data-testid="touchpoint-fu-date" type="date" value={fuForm.date} onChange={e => setFuForm(f => ({ ...f, date: e.target.value }))} className="mt-1" /></div>
            <div><Label>Notes</Label><Textarea data-testid="touchpoint-fu-note" value={fuForm.note} onChange={e => setFuForm(f => ({ ...f, note: e.target.value }))} className="mt-1" placeholder="Optional details..." /></div>
            <Button data-testid="touchpoint-fu-submit" onClick={addFollowUp} className="w-full bg-blue-700 hover:bg-blue-800">Log Follow-Up</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
